import React, { useState } from 'react';
import api from '../../services/api';
import { useSelector } from 'react-redux';

function escapeCsvField(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function splitFirstLastName(fullName) {
  const t = (fullName || '').trim();
  if (!t) return { first: '', last: '' };
  const i = t.indexOf(' ');
  if (i === -1) return { first: t, last: '' };
  return { first: t.slice(0, i), last: t.slice(i + 1).trim() };
}

const TASK_GROUPS = [
  {
    title: 'Database',
    tasks: [
      { id: 'maintenanceDryRun', label: 'MaintenanceDB - DryRun' },
      { id: 'maintenanceDB', label: 'MaintenanceDB' },
      { id: 'checkUsers', label: 'CheckUsers' }
    ]
  },
  {
    title: 'Monitoring',
    tasks: [
      { id: 'clientSummary', label: 'ClientSummary' },
      { id: 'listLicenseRequests', label: 'ListLicenseRequests' },
      { id: 'exportLicenseRequests', label: 'ExportLicenseRequests' },
      {
        id: 'listClientsWithoutLicenseRequest',
        label: 'Clients sans demande de licence',
        color: 'bg-amber-600'
      }
    ]
  },
  {
    title: 'Tests',
    tasks: [
      { id: 'testLicenseEndToEnd', label: '🧪 Test E2E - Chaîne Complète Licences', color: 'bg-purple-600' }
    ]
  }
];

const AdminMaintenance = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isExportingClients, setIsExportingClients] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [machineId, setMachineId] = useState('');
  const [licenseResult, setLicenseResult] = useState(null);
  const [isSearchingLicense, setIsSearchingLicense] = useState(false);
  const { userInfo } = useSelector((state) => state.auth);

  const exportClientsCsv = async () => {
    const authHeaders = { Authorization: `Bearer ${userInfo.token}` };
    const pageSize = 500;
    try {
      setIsExportingClients(true);
      setError('');
      const { data: firstPage } = await api.get('/admin/users', {
        params: {
          page: 1,
          limit: pageSize,
          sortBy: 'email',
          sortOrder: 'asc',
          search: ''
        },
        headers: authHeaders
      });
      const allUsers = [...(firstPage.users || [])];
      const totalPages = firstPage.pagination?.total ?? 0;
      for (let p = 2; p <= totalPages; p++) {
        const { data } = await api.get('/admin/users', {
          params: {
            page: p,
            limit: pageSize,
            sortBy: 'email',
            sortOrder: 'asc',
            search: ''
          },
          headers: authHeaders
        });
        allUsers.push(...(data.users || []));
      }
      const clients = allUsers.filter((u) => !u.isAdmin);
      const header = ['Email', 'First Name', 'Last Name'];
      const lines = [header.map(escapeCsvField).join(',')];
      for (const u of clients) {
        const { first, last } = splitFirstLastName(u.name);
        lines.push([u.email, first, last].map(escapeCsvField).join(','));
      }
      const csv = '\ufeff' + lines.join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `clients-alturion-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setError(typeof msg === 'string' ? msg : 'Erreur lors de l’export CSV');
    } finally {
      setIsExportingClients(false);
    }
  };

  const runTask = async (taskId) => {
    try {
      setIsRunning(true);
      setOutput('');
      setError('');
      const { data } = await api.post(
        '/admin/maintenance/run',
        { task: taskId },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      setOutput((data.stdout || '') + (data.stderr ? `\n[stderr]\n${data.stderr}` : ''));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setOutput(err.response?.data?.stderr || '');
    } finally {
      setIsRunning(false);
    }
  };

  const createRecoveryLink = async () => {
    if (!orderId.trim()) {
      setError('Veuillez saisir un ID de commande');
      return;
    }

    try {
      setIsCreatingLink(true);
      setError('');
      setRecoveryResult(null);
      
      const { data } = await api.post(
        '/admin/recovery/create-payment-link',
        { orderId: orderId.trim() },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      
      setRecoveryResult(data);
      setOrderId(''); // Clear input after success
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setRecoveryResult(null);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const searchLicense = async () => {
    if (!machineId.trim()) {
      setError('Veuillez saisir un Machine ID');
      return;
    }

    try {
      setIsSearchingLicense(true);
      setError('');
      setLicenseResult(null);
      
      const { data } = await api.get(
        `/admin/license-requests/search?machineId=${encodeURIComponent(machineId.trim())}`,
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      
      setLicenseResult(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setLicenseResult(null);
    } finally {
      setIsSearchingLicense(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'À traiter' },
      approved: { color: 'bg-blue-100 text-blue-800', text: 'Approuvée' },
      exported: { color: 'bg-indigo-100 text-indigo-800', text: 'Exportée au robot' },
      installed: { color: 'bg-green-100 text-green-800', text: 'Installée' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejetée' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">📥 Export clients (CSV)</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Colonnes : <code className="text-xs bg-gray-100 px-1 rounded">Email</code>,{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">First Name</code>,{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">Last Name</code> (dérivés du champ
            name). Comptes admin exclus. Données récupérées via l’API{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">/admin/users</code> (fonctionne sans
            déploiement backend supplémentaire).
          </p>
          <button
            type="button"
            onClick={exportClientsCsv}
            disabled={isExportingClients}
            className={`px-5 py-2 rounded-md font-medium text-white transition ${
              isExportingClients
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {isExportingClients ? '⏳ Export…' : '⬇️ Télécharger clients.csv'}
          </button>
        </div>
      </div>

      {/* License Search Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">🔍 Recherche de Licence par Machine ID</h3>
        </div>
        <div className="p-6">
          <div className="max-w-2xl">
            <p className="text-sm text-gray-600 mb-4">
              Rechercher une licence par son Machine ID pour voir tous les détails (statut, dates, utilisateur, produit, etc.)
            </p>
            
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label htmlFor="machineId" className="block text-sm font-medium text-gray-700 mb-2">
                  Machine ID
                </label>
                <input
                  type="text"
                  id="machineId"
                  value={machineId}
                  onChange={(e) => setMachineId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchLicense()}
                  placeholder="WATCHDOG41354-SamyTest"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSearchingLicense}
                />
              </div>
              <button
                onClick={searchLicense}
                disabled={isSearchingLicense || !machineId.trim()}
                className={`px-6 py-2 rounded-md font-medium transition ${
                  isSearchingLicense || !machineId.trim()
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isSearchingLicense ? '⏳ Recherche...' : '🔍 Rechercher'}
              </button>
            </div>

            {/* License Result */}
            {licenseResult && (
              <div className={`mt-6 p-6 border rounded-lg ${
                licenseResult.found 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {licenseResult.found ? (
                  <>
                    <h4 className="text-lg font-medium text-green-800 mb-4">✅ Licence trouvée !</h4>
                    <div className="space-y-4 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="font-medium text-gray-700">ID:</p>
                          <p className="text-gray-600 font-mono text-xs">{licenseResult.licenseRequest._id}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Machine ID:</p>
                          <p className="text-gray-600 font-mono">{licenseResult.licenseRequest.machineIdName}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Statut:</p>
                          <div>{getStatusBadge(licenseResult.licenseRequest.status)}</div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Créée le:</p>
                          <p className="text-gray-600">{formatDate(licenseResult.licenseRequest.createdAt)}</p>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <p className="font-medium text-gray-700 mb-2">👤 Utilisateur:</p>
                        <p className="text-gray-600">{licenseResult.licenseRequest.user?.name || 'N/A'}</p>
                        <p className="text-gray-600 text-xs">{licenseResult.licenseRequest.user?.email || 'N/A'}</p>
                      </div>

                      <div className="border-t pt-4">
                        <p className="font-medium text-gray-700 mb-2">📦 Produit:</p>
                        <p className="text-gray-600">{licenseResult.licenseRequest.product?.name || 'N/A'}</p>
                        <p className="text-gray-600 text-xs">Slug: {licenseResult.licenseRequest.product?.slug || 'N/A'}</p>
                      </div>

                      <div className="border-t pt-4">
                        <p className="font-medium text-gray-700 mb-2">📅 Dates:</p>
                        <div className="space-y-1 text-gray-600">
                          {licenseResult.licenseRequest.processedAt && (
                            <p>Traitée le: {formatDate(licenseResult.licenseRequest.processedAt)}</p>
                          )}
                          {licenseResult.licenseRequest.exportedAt && (
                            <p className="text-blue-600">📤 Exportée le: {formatDate(licenseResult.licenseRequest.exportedAt)}</p>
                          )}
                          {licenseResult.licenseRequest.installedAt && (
                            <p className="text-green-600">✅ Installée le: {formatDate(licenseResult.licenseRequest.installedAt)}</p>
                          )}
                        </div>
                      </div>

                      {licenseResult.licenseRequest.licenseKey && (
                        <div className="border-t pt-4">
                          <p className="font-medium text-gray-700 mb-2">🔑 Clé de licence:</p>
                          <p className="text-gray-600 font-mono text-xs bg-gray-100 p-2 rounded">{licenseResult.licenseRequest.licenseKey}</p>
                        </div>
                      )}

                      {licenseResult.licenseRequest.vendorResponse && (
                        <div className="border-t pt-4">
                          <p className="font-medium text-gray-700 mb-2">💬 Message VPS:</p>
                          <p className="text-gray-600 text-xs bg-blue-50 p-2 rounded">{licenseResult.licenseRequest.vendorResponse}</p>
                        </div>
                      )}

                      {licenseResult.licenseRequest.adminNotes && (
                        <div className="border-t pt-4">
                          <p className="font-medium text-gray-700 mb-2">📝 Notes admin:</p>
                          <p className="text-gray-600 text-xs bg-gray-50 p-2 rounded">{licenseResult.licenseRequest.adminNotes}</p>
                        </div>
                      )}

                      {licenseResult.licenseRequest.order && (
                        <div className="border-t pt-4">
                          <p className="font-medium text-gray-700 mb-2">💰 Commande:</p>
                          <p className="text-gray-600 text-xs">ID: {licenseResult.licenseRequest.order._id}</p>
                          <p className="text-gray-600 text-xs">Date: {formatDate(licenseResult.licenseRequest.order.createdAt)}</p>
                          <p className="text-gray-600 text-xs">Total: {licenseResult.licenseRequest.order.totalPrice?.toFixed(2) || 'N/A'}€</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : licenseResult.partialMatches ? (
                  <>
                    <h4 className="text-lg font-medium text-yellow-800 mb-4">⚠️ Correspondances partielles trouvées</h4>
                    <div className="space-y-3">
                      {licenseResult.partialMatches.map((match, index) => (
                        <div key={match._id} className="bg-white p-3 rounded border border-yellow-200">
                          <p className="font-medium text-gray-700">{index + 1}. {match.machineIdName}</p>
                          <div className="mt-2 flex gap-4 text-xs text-gray-600">
                            <span>Statut: {getStatusBadge(match.status)}</span>
                            <span>Date: {formatDate(match.createdAt)}</span>
                            <span>User: {match.user?.name || 'N/A'}</span>
                            <span>Produit: {match.product?.name || 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <h4 className="text-lg font-medium text-yellow-800">❌ Aucune licence trouvée</h4>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Recovery Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">🔄 Récupération de Commandes</h3>
        </div>
        <div className="p-6">
          <div className="max-w-2xl">
            <p className="text-sm text-gray-600 mb-4">
              Créer un nouveau lien de paiement pour une commande non finalisée (échec de paiement, abandon de panier, etc.)
            </p>
            
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label htmlFor="orderId" className="block text-sm font-medium text-gray-700 mb-2">
                  ID de la commande
                </label>
                <input
                  type="text"
                  id="orderId"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="691da710fa8542ad184bb09f"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isCreatingLink}
                />
              </div>
              <button
                onClick={createRecoveryLink}
                disabled={isCreatingLink || !orderId.trim()}
                className={`px-6 py-2 rounded-md font-medium transition ${
                  isCreatingLink || !orderId.trim()
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {isCreatingLink ? '⏳ Création...' : '🔗 Créer le lien'}
              </button>
            </div>

            {/* Recovery Result */}
            {recoveryResult && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-lg font-medium text-green-800 mb-3">✅ Lien créé avec succès !</h4>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Commande:</p>
                    <p className="text-sm text-gray-600">
                      {recoveryResult.order.customer} - {recoveryResult.order.total}€
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700">Lien de paiement:</p>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={recoveryResult.paymentLink}
                        readOnly
                        className="flex-1 px-2 py-1 text-xs bg-white border border-gray-300 rounded"
                      />
                      <button
                        onClick={() => navigator.clipboard.writeText(recoveryResult.paymentLink)}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        📋 Copier
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Template d'email:</p>
                    <div className="bg-white border border-gray-300 rounded p-3 text-xs">
                      <p><strong>À:</strong> {recoveryResult.emailTemplate.to}</p>
                      <p><strong>Objet:</strong> {recoveryResult.emailTemplate.subject}</p>
                      <div className="mt-2 whitespace-pre-line">{recoveryResult.emailTemplate.body}</div>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(
                        `À: ${recoveryResult.emailTemplate.to}\nObjet: ${recoveryResult.emailTemplate.subject}\n\n${recoveryResult.emailTemplate.body}`
                      )}
                      className="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      📋 Copier l'email
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Existing Task Groups */}
      {TASK_GROUPS.map((group) => (
        <div key={group.title} className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{group.title}</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.tasks.map((t) => (
              <button
                key={t.id}
                onClick={() => runTask(t.id)}
                disabled={isRunning}
                className={`px-4 py-3 rounded text-white font-medium hover:opacity-90 transition ${
                  isRunning ? 'bg-gray-400 cursor-not-allowed' : (t.color || 'bg-blue-600')
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Résultats</h3>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}
          <pre className="bg-gray-900 text-green-200 text-xs p-4 rounded overflow-auto max-h-96 whitespace-pre-wrap">
            {output || 'Cliquez sur une tâche pour voir la sortie ici...'}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default AdminMaintenance;


